import { supabase } from '../lib/supabase';

// ============================================================================
// ERROR HANDLING UTILITY
// ============================================================================

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// ============================================================================
// CASE CONVERSION UTILITIES
// ============================================================================

function toSnakeCase(str) {
  return str?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
}

function toCamelCase(str) {
  return str?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
}

function convertKeysToSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(convertKeysToSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = toSnakeCase(key);
    acc[snakeKey] = convertKeysToSnakeCase(obj?.[key]);
    return acc;
  }, {});
}

function convertKeysToCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(convertKeysToCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = toCamelCase(key);
    acc[camelKey] = convertKeysToCamelCase(obj?.[key]);
    return acc;
  }, {});
}

// ============================================================================
// STORAGE SERVICE — Upload images to Supabase Storage instead of DB
// ============================================================================

export const storageService = {
  /**
   * Upload a base64 data URL to Supabase Storage bucket 'generated-images'
   * Returns the public URL of the uploaded file
   * @param {string} base64DataUrl - "data:image/png;base64,..." or regular URL
   * @param {string} userId - user ID for folder organization
   * @returns {string} public URL of the uploaded image
   */
  async uploadImage(base64DataUrl, userId) {
    try {
      // Skip if already a storage URL (not base64)
      if (base64DataUrl && !base64DataUrl.startsWith('data:')) {
        return base64DataUrl;
      }

      if (!base64DataUrl || !base64DataUrl.startsWith('data:')) {
        console.error('[STORAGE] Invalid base64 data URL');
        return base64DataUrl;
      }

      // Extract mime type and base64 data
      const matches = base64DataUrl.match(/^data:([^;]+);base64,(.+)$/);
      if (!matches) {
        console.error('[STORAGE] Could not parse base64 data URL');
        return base64DataUrl;
      }

      const mimeType = matches[1];
      const base64Data = matches[2];

      // Determine file extension
      const extMap = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/webp': 'webp',
        'image/gif': 'gif',
      };
      const ext = extMap[mimeType] || 'png';

      // Convert base64 to Uint8Array for upload
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Generate unique filename: userId/timestamp-random.ext
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      const filePath = `${userId}/${timestamp}-${random}.${ext}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('generated-images')
        .upload(filePath, bytes, {
          contentType: mimeType,
          cacheControl: '31536000', // 1 year cache
          upsert: false,
        });

      if (error) {
        console.error('[STORAGE] Upload error:', error.message);
        return null;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('generated-images')
        .getPublicUrl(data.path);

      console.log('[STORAGE] Uploaded successfully:', urlData.publicUrl);
      return urlData.publicUrl;
    } catch (error) {
      console.error('[STORAGE] Upload failed:', error);
      return null;
    }
  },

  /**
   * Upload a File object to Supabase Storage bucket 'generated-images'
   * Returns the public URL of the uploaded file
   * @param {File} file - File object from input or drag-drop
   * @param {string} userId - user ID for folder organization
   * @returns {string} public URL of the uploaded image
   */
  async uploadFile(file, userId) {
    try {
      if (!file || !userId) return null;

      const ext = file.name?.split('.').pop()?.toLowerCase() || 'png';
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      const filePath = `${userId}/input-${timestamp}-${random}.${ext}`;

      const { data, error } = await supabase.storage
        .from('generated-images')
        .upload(filePath, file, {
          contentType: file.type || 'image/png',
          cacheControl: '31536000',
          upsert: false,
        });

      if (error) {
        console.error('[STORAGE] File upload error:', error.message);
        return null;
      }

      const { data: urlData } = supabase.storage
        .from('generated-images')
        .getPublicUrl(data.path);

      return urlData.publicUrl;
    } catch (error) {
      console.error('[STORAGE] File upload failed:', error);
      return null;
    }
  },

  /**
   * Delete an image from Supabase Storage bucket 'generated-images'
   * @param {string} publicUrl - public URL of the image
   */
  async deleteImage(publicUrl) {
    try {
      if (!publicUrl) return;
      // Extract file path from public URL
      const match = publicUrl.match(/generated-images\/(.+?)(?:\?|$)/);
      if (!match) return;
      const filePath = decodeURIComponent(match[1]);
      const { error } = await supabase.storage
        .from('generated-images')
        .remove([filePath]);
      if (error) {
        console.error('[STORAGE] Delete error:', error.message);
      }
    } catch (error) {
      console.error('[STORAGE] Delete failed:', error);
    }
  },
};

// ============================================================================
// USER PROFILES SERVICE
// ============================================================================

export const userProfilesService = {
  async getProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return null;
        console.log('Get profile error:', error?.message);
        return null;
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Schema error in getProfile:', error?.message);
      throw error;
    }
  },

  async updateProfile(userId, updates) {
    try {
      const snakeCaseUpdates = convertKeysToSnakeCase(updates);
      
      const { data, error } = await supabase?.from('user_profiles')?.update(snakeCaseUpdates)?.eq('id', userId)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Update profile error:', error?.message);
        return { data: null, error };
      }
      
      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      console.error('Schema error in updateProfile:', error?.message);
      throw error;
    }
  },

  async updateCredits(_userId, creditsUsed) {
    // Deduct credits via edge function (user JWT can't update remaining_credits
    // directly because of the check_user_profile_update_allowed trigger).
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return { data: null, error: { message: 'Not authenticated' } };

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-credits`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ action: 'deduct', creditsUsed }),
        }
      );

      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { data: null, error: { message: result?.error || 'Failed to deduct credits' } };
      }
      return { data: convertKeysToCamelCase(result.profile), error: null };
    } catch (error) {
      console.error('updateCredits error:', error?.message);
      return { data: null, error: { message: 'Network error' } };
    }
  },

  async checkCredits(userId, creditsNeeded) {
    try {
      const { data: profile } = await supabase?.from('user_profiles')?.select('remaining_credits, subscription_tier')?.eq('id', userId)?.single();
      if (!profile) return { allowed: false, available: 0, message: 'Profile not found' };

      // Team member: check both member cap and owner pool via secure DB function
      if (profile.subscription_tier === 'team_member') {
        if (profile.remaining_credits < creditsNeeded) {
          return { allowed: false, available: profile.remaining_credits, message: `You have reached your credit limit. You have ${profile.remaining_credits} credit(s) remaining from your allowance.` };
        }

        const { data: ownerRemaining } = await supabase.rpc('get_team_owner_remaining_credits');
        if (ownerRemaining === null || ownerRemaining === undefined || ownerRemaining < creditsNeeded) {
          return { allowed: false, available: 0, message: 'Your team has run out of credits. Please contact your team owner.' };
        }

        return { allowed: true, available: Math.min(profile.remaining_credits, ownerRemaining) };
      }

      // Regular user
      if (profile.remaining_credits < creditsNeeded) {
        return { allowed: false, available: profile.remaining_credits, message: `Insufficient credits. You need ${creditsNeeded} but have ${profile.remaining_credits}. Please upgrade your plan.` };
      }

      return { allowed: true, available: profile.remaining_credits };
    } catch (error) {
      console.error('Error checking credits:', error?.message);
      return { allowed: false, available: 0, message: 'Error checking credits' };
    }
  }
};

// ============================================================================
// PROJECTS SERVICE
// ============================================================================

export const projectsService = {
  // Team owner: fetch paginated projects (own + members') via SECURITY DEFINER RPC
  // Returns { data: [], count: number }
  async getTeamProjects(filters = {}, page = 1, limit = 10) {
    try {
      const offset = (page - 1) * limit;
      const [projectsRes, countRes] = await Promise.all([
        supabase?.rpc('get_team_all_projects', {
          p_tool_type: filters?.toolType || null,
          p_status:    filters?.status    || null,
          p_search:    filters?.search    || null,
          p_user_id:   filters?.userId    || null,
          p_limit:     limit,
          p_offset:    offset,
        }),
        supabase?.rpc('get_team_projects_count', {
          p_tool_type: filters?.toolType || null,
          p_status:    filters?.status    || null,
          p_search:    filters?.search    || null,
          p_user_id:   filters?.userId    || null,
        }),
      ]);
      if (projectsRes.error) {
        if (isSchemaError(projectsRes.error)) throw projectsRes.error;
        console.log('Get team projects error:', projectsRes.error?.message);
        return { data: [], count: 0 };
      }
      return {
        data:  projectsRes.data?.map(convertKeysToCamelCase) || [],
        count: countRes.data ?? 0,
      };
    } catch (error) {
      console.error('Schema error in getTeamProjects:', error?.message);
      throw error;
    }
  },

  // Regular user: fetch paginated own projects
  // Returns { data: [], count: number }
  async getProjects(userId, filters = {}, page = 1, limit = 10) {
    try {
      const offset = (page - 1) * limit;
      let query = supabase?.from('projects')
        ?.select('id, name, user_id, thumbnail, tool_type, tool_name, status, variations, tags, description, created_at, updated_at', { count: 'exact' })
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.range(offset, offset + limit - 1);

      if (filters?.toolType) query = query?.eq('tool_type', filters?.toolType);
      if (filters?.status)   query = query?.eq('status', filters?.status);
      if (filters?.search)   query = query?.ilike('name', `%${filters?.search}%`);

      const { data, error, count } = await query;

      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return { data: [], count: 0 };
        console.log('Get projects error:', error?.message);
        return { data: [], count: 0 };
      }

      return { data: data?.map(convertKeysToCamelCase) || [], count: count || 0 };
    } catch (error) {
      console.error('Schema error in getProjects:', error?.message);
      throw error;
    }
  },

  // Batch design counts — single query replaces N+1 loop
  // Returns { [projectId]: count }
  async getProjectsDesignCounts(projectIds) {
    if (!projectIds?.length) return {};
    const { data: rows } = await supabase
      ?.from('saved_designs')
      ?.select('project_id')
      ?.in('project_id', projectIds)
      ?.not('image_url', 'is', null)
      ?.neq('image_url', '');
    const map = {};
    (rows || []).forEach(row => { map[row.project_id] = (map[row.project_id] || 0) + 1; });
    return map;
  },

  async getProject(projectId) {
    try {
      const { data, error } = await supabase?.from('projects')?.select('*')?.eq('id', projectId)?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return null;
        console.log('Get project error:', error?.message);
        return null;
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Schema error in getProject:', error?.message);
      throw error;
    }
  },

  async createProject(userId, projectData) {
    try {
      // Check project limit before creating
      {
        const { data: profile } = await supabase?.from('user_profiles')?.select('subscription_tier')?.eq('id', userId)?.single();
        const { checkProjectLimit } = await import('../utils/planLimits.js');
        const { allowed, current, max } = await checkProjectLimit(userId, profile?.subscription_tier);
        if (!allowed) {
          return { data: null, error: { message: `Project limit reached (${current}/${max}). Please upgrade your plan or delete existing projects.`, limitReached: true } };
        }
      }

      const snakeCaseData = convertKeysToSnakeCase({
        ...projectData,
        userId
      });

      const { data, error } = await supabase?.from('projects')?.insert(snakeCaseData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Create project error:', error?.message);
        return { data: null, error };
      }
      
      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      console.error('Schema error in createProject:', error?.message);
      throw error;
    }
  },

  async updateProject(projectId, updates) {
    try {
      const snakeCaseUpdates = convertKeysToSnakeCase(updates);
      
      const { data, error } = await supabase?.from('projects')?.update(snakeCaseUpdates)?.eq('id', projectId)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Update project error:', error?.message);
        return { data: null, error };
      }
      
      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      console.error('Schema error in updateProject:', error?.message);
      throw error;
    }
  },

  async deleteProject(projectId) {
    try {
      const { error } = await supabase?.from('projects')?.delete()?.eq('id', projectId);
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Delete project error:', error?.message);
        return { error };
      }
      
      return { error: null };
    } catch (error) {
      console.error('Schema error in deleteProject:', error?.message);
      throw error;
    }
  },

  async incrementVariations(projectId, count = 1) {
    try {
      const { data: project } = await supabase?.from('projects')?.select('variations')?.eq('id', projectId)?.single();

      if (!project) return { error: { message: 'Project not found' } };

      const { data, error } = await supabase?.from('projects')?.update({ variations: (project?.variations || 0) + count })?.eq('id', projectId)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Increment variations error:', error?.message);
        return { data: null, error };
      }
      
      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      console.error('Schema error in incrementVariations:', error?.message);
      throw error;
    }
  }
};

// ============================================================================
// SAVED DESIGNS SERVICE
// ============================================================================

export const savedDesignsService = {
  async getDesigns(projectId) {
    try {
      const { data, error } = await supabase?.from('saved_designs')?.select('id, image_url, prompt, project_id, created_at')?.eq('project_id', projectId)?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return [];
        console.log('Get designs error:', error?.message);
        return [];
      }
      
      return data?.map(convertKeysToCamelCase) || [];
    } catch (error) {
      console.error('Schema error in getDesigns:', error?.message);
      throw error;
    }
  },

  async createDesign(userId, projectId, designData) {
    try {
      // Check storage limit before creating
      {
        const { data: profile } = await supabase?.from('user_profiles')?.select('subscription_tier')?.eq('id', userId)?.single();
        const { checkSaveDesignLimit } = await import('../utils/planLimits.js');
        const { allowed, current, max } = await checkSaveDesignLimit(userId, profile?.subscription_tier);
        if (!allowed) {
          return { data: null, error: { message: `Storage limit reached (${current}/${max}). Please upgrade your plan or delete existing files.`, limitReached: true } };
        }
      }

      const snakeCaseData = convertKeysToSnakeCase({
        ...designData,
        userId,
        projectId
      });

      const { data, error } = await supabase?.from('saved_designs')?.insert(snakeCaseData)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Create design error:', error?.message);
        return { data: null, error };
      }
      
      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      console.error('Schema error in createDesign:', error?.message);
      throw error;
    }
  },

  async toggleFavorite(designId, isFavorite) {
    try {
      const { data, error } = await supabase?.from('saved_designs')?.update({ is_favorite: isFavorite })?.eq('id', designId)?.select()?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Toggle favorite error:', error?.message);
        return { data: null, error };
      }
      
      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      console.error('Schema error in toggleFavorite:', error?.message);
      throw error;
    }
  },

  async deleteDesign(designId) {
    try {
      const { error } = await supabase?.from('saved_designs')?.delete()?.eq('id', designId);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Delete design error:', error?.message);
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Schema error in deleteDesign:', error?.message);
      throw error;
    }
  },

  async deleteByImageUrl(imageUrl) {
    try {
      if (!imageUrl) return;
      // Use RPC to send image_url via POST body instead of query string
      // to avoid ERR_CONNECTION_RESET from overly long DELETE request URLs
      const { error } = await supabase.rpc('delete_saved_design_by_url', {
        p_image_url: imageUrl,
      });
      if (error) console.log('Delete by image_url error:', error?.message);
    } catch (error) {
      console.error('Error deleting by image_url:', error?.message);
    }
  }
};

// ============================================================================
// GENERATION METADATA SERVICE
// ============================================================================

export const generationMetadataService = {
  async getMetadata(designId) {
    try {
      const { data, error } = await supabase?.from('generation_metadata')?.select('*')?.eq('design_id', designId)?.single();
      
      if (error) {
        if (isSchemaError(error)) throw error;
        if (error?.code === 'PGRST116') return null;
        console.log('Get metadata error:', error?.message);
        return null;
      }
      
      return convertKeysToCamelCase(data);
    } catch (error) {
      console.error('Schema error in getMetadata:', error?.message);
      throw error;
    }
  },

  async updateUsedMedia(designId, usedMedia) {
    try {
      const { error } = await supabase
        ?.from('generation_metadata')
        ?.update({ used_media: usedMedia })
        ?.eq('design_id', designId);
      if (error) console.log('Update usedMedia error:', error?.message);
    } catch (error) {
      console.error('Error updating usedMedia:', error?.message);
    }
  },

  async createMetadata(userId, designId, metadataData) {
    try {
      const snakeCaseData = convertKeysToSnakeCase({
        ...metadataData,
        userId,
        designId
      });

      const { data, error } = await supabase?.from('generation_metadata')?.insert(snakeCaseData)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Create metadata error:', error?.message);
        return { data: null, error };
      }

      return { data: convertKeysToCamelCase(data), error: null };
    } catch (error) {
      console.error('Schema error in createMetadata:', error?.message);
      throw error;
    }
  },

  async removeFromUsedMedia(designId, imageUrl) {
    try {
      const { data } = await supabase
        ?.from('generation_metadata')
        ?.select('used_media')
        ?.eq('design_id', designId)
        ?.single();
      if (data?.used_media) {
        const updated = data.used_media.filter((url) => url !== imageUrl);
        await supabase
          ?.from('generation_metadata')
          ?.update({ used_media: updated })
          ?.eq('design_id', designId);
      }
    } catch (error) {
      console.error('Error removing from usedMedia:', error?.message);
    }
  }
};

// ============================================================================
// AUTHENTICATION SERVICE
// ============================================================================

export const authService = {
  async signUp(email, password, metadata = {}) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });
      
      if (error) {
        console.log('Sign up error:', error?.message);
        return { data: null, error };
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error?.message);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.log('Sign in error:', error?.message);
        return { data: null, error };
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error?.message);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut({ scope: 'local' });
      
      if (error) {
        console.log('Sign out error:', error?.message);
        return { error };
      }
      
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error?.message);
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  async getUser() {
    try {
      const { data: { user }, error } = await supabase?.auth?.getUser();
      
      if (error) {
        console.log('Get user error:', error?.message);
        return { user: null, error };
      }
      
      return { user, error: null };
    } catch (error) {
      console.error('Get user error:', error?.message);
      return { user: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  async resetPassword(email) {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) {
        console.log('Reset password error:', error?.message);
        return { error };
      }
      
      return { error: null };
    } catch (error) {
      console.error('Reset password error:', error?.message);
      return { error: { message: 'Network error. Please try again.' } };
    }
  },

  async updatePassword(newPassword) {
    try {
      const { error } = await supabase?.auth?.updateUser({
        password: newPassword
      });
      
      if (error) {
        console.log('Update password error:', error?.message);
        return { error };
      }
      
      return { error: null };
    } catch (error) {
      console.error('Update password error:', error?.message);
      return { error: { message: 'Network error. Please try again.' } };
    }
  }
};